import { Injectable, signal, computed } from '@angular/core';
import { Tag, User, isBle } from '../models/tag.model';

@Injectable({ providedIn: 'root' })
export class TagService {
  tags = signal<Tag[]>([]);
  pendingSelectId: string | null = null;

  /** True si le compte possède au moins un device de type BLE. */
  hasBle = computed(() => this.tags().some(t => isBle(t.mac)));

  users = signal<User[]>([
    { id: 'user-1', name: 'Alex Laurent', initials: 'AL', email: 'a.laurent@translog.fr', role: 'admin', active: true, avatarGradient: 'linear-gradient(135deg,#c10000,#e05070)', tagIds: ['PAL-001', 'PAL-002', 'AST-005'] },
    { id: 'user-2', name: 'Sophie Martin', initials: 'SM', email: 's.martin@logitec.fr', role: 'operator', active: true, avatarGradient: 'linear-gradient(135deg,#4caf82,#2e8a5a)', tagIds: ['AST-003', 'PAL-004'] },
    { id: 'user-3', name: 'Pierre Dubois', initials: 'PD', email: 'p.dubois@supply.fr', role: 'viewer', active: true, avatarGradient: 'linear-gradient(135deg,#6b6560,#a09a94)', tagIds: ['PAL-006', 'PAL-007'] },
    { id: 'user-4', name: 'Marie Caron', initials: 'MC', email: 'm.caron@logitec.fr', role: 'operator', active: false, avatarGradient: 'linear-gradient(135deg,#5b8dee,#3a6bc4)', tagIds: ['AST-008'] },
  ]);

  selectedTag = signal<Tag | null>(null);

  reset(): void {
    this.tags.set([]);
    this.selectedTag.set(null);
    this.pendingSelectId = null;
  }

  setDevices(devices: Tag[]): void {
    this.tags.set(devices);
    const pending = this.pendingSelectId;
    this.pendingSelectId = null;
    const target = pending ? (devices.find(t => t.id === pending) ?? devices[0]) : devices[0];
    this.selectedTag.set(target ?? null);
  }

  mergeDevices(updates: Tag[]): void {
    if (updates.length === 0) return;
    const updateMap = new Map(updates.map(t => [t.id, t]));
    this.tags.update(current => current.map(t => updateMap.get(t.id) ?? t));
    const sel = this.selectedTag();
    if (sel && updateMap.has(sel.id)) this.selectedTag.set(updateMap.get(sel.id)!);
  }

  patchTag(id: string, patch: Partial<Tag>): void {
    this.tags.update(current => current.map(t => t.id === id ? { ...t, ...patch } : t));
    const sel = this.selectedTag();
    if (sel?.id === id) this.selectedTag.set({ ...sel, ...patch });
  }

  selectTag(tag: Tag): void {
    this.selectedTag.set(tag);
  }

  get onlineCount(): number { return this.tags().filter(t => t.status === 'online').length; }
  get warningCount(): number { return this.tags().filter(t => t.status === 'warning').length; }
  get offlineCount(): number { return this.tags().filter(t => t.status === 'offline').length; }
  get bleCount(): number { return this.tags().filter(t => isBle(t.mac)).length; }
}
